import { useEffect, useState } from "react";
import { Bell, User, ChevronDown, Settings, LogOut } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/hooks/useAuth";
import { notificationService } from "@/services/notificationService";

export function Header() {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);

  const loadUnreadNotifications = async () => {
    try {
      const response = await notificationService.getUnread();

      const notifications =
        Array.isArray(response?.data)
          ? response.data
          : Array.isArray(response)
            ? response
            : [];

      setUnreadCount(notifications.length);
    } catch (error) {
      console.error("Error loading unread notifications:", error);
    }
  };

  useEffect(() => {
    loadUnreadNotifications();

    // تحديث عدد الإشعارات كل 30 ثانية
    const interval = setInterval(loadUnreadNotifications, 30000);

    return () => clearInterval(interval);
  }, []);

  const handleLogout = () => {
    setUserMenuOpen(false);
    logout();
    navigate("/login");
  };

  return (
    <header className="h-20 bg-white border-b border-gray-200 flex items-center justify-between px-6 sticky top-0 z-30">
      <div className="flex items-center gap-4">
        <img
          src="/logo-light.png"
          alt="Target Express"
          className="h-14 w-auto object-contain hidden sm:block"
        />

        <button
          type="button"
          onClick={() => navigate("/dashboard")}
          className="text-2xl font-bold text-gray-800 hover:text-primary transition-colors"
        >
          لوحة التحكم
        </button>
      </div>

      <div className="flex items-center gap-3">
        {/* الإشعارات */}
        <button
          type="button"
          onClick={() => navigate("/notifications")}
          className="relative p-2 rounded-lg hover:bg-gray-100 transition-colors"
          title="الإشعارات"
          aria-label="الإشعارات"
        >
          <Bell className="w-5 h-5 text-gray-600" />

          {unreadCount > 0 && (
            <span
              className="
                absolute -top-1 -right-1
                min-w-[20px] h-5
                px-1
                rounded-full
                bg-red-500
                text-white
                text-[11px]
                font-bold
                flex items-center justify-center
                border-2 border-white
                shadow-sm
              "
            >
              {unreadCount > 99 ? "99+" : unreadCount}
            </span>
          )}
        </button>

        {/* المستخدم */}
        <div className="relative">
          <button
            type="button"
            onClick={() => setUserMenuOpen((prev) => !prev)}
            className="flex items-center gap-3 px-3 py-2 rounded-lg bg-gray-50 hover:bg-gray-100 transition-colors"
            aria-label="قائمة المستخدم"
          >
            <div className="w-10 h-10 rounded-full bg-primary flex items-center justify-center">
              <User className="w-5 h-5 text-white" />
            </div>

            <div className="hidden sm:block text-right">
              <div className="text-base font-semibold text-gray-800">
                {user?.fullName || user?.username || "المستخدم"}
              </div>

              <div className="text-sm text-gray-500">
                {user?.roleName || user?.role || "مدير النظام"}
              </div>
            </div>

            <ChevronDown
              className={`w-4 h-4 text-gray-500 transition-transform ${
                userMenuOpen ? "rotate-180" : ""
              }`}
            />
          </button>

          {userMenuOpen && (
            <>
              <div
                className="fixed inset-0 z-40"
                onClick={() => setUserMenuOpen(false)}
              />

              <div className="absolute left-0 top-full mt-2 w-56 bg-white border border-gray-200 rounded-xl shadow-lg z-50 overflow-hidden">
                <div className="px-4 py-3 border-b bg-gray-50">
                  <p className="font-semibold text-gray-800">
                    {user?.fullName || user?.username || "المستخدم"}
                  </p>

                  <p className="text-xs text-gray-500 mt-1">
                    {user?.roleName || user?.role || "مدير النظام"}
                  </p>
                </div>

                <button
                  type="button"
                  onClick={() => {
                    setUserMenuOpen(false);
                    navigate("/profile");
                  }}
                  className="w-full flex items-center gap-3 px-4 py-3 text-sm text-gray-700 hover:bg-gray-50"
                >
                  <Settings className="w-4 h-4" />
                  الإعدادات
                </button>

                <button
                  type="button"
                  onClick={handleLogout}
                  className="w-full flex items-center gap-3 px-4 py-3 text-sm text-red-600 hover:bg-red-50 border-t"
                >
                  <LogOut className="w-4 h-4" />
                  تسجيل الخروج
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </header>
  );
}
